import React, { useCallback, useState } from 'react';
import { UploadCloudIcon, FileIcon, XIcon } from 'lucide-react';
import { motion } from 'framer-motion';
interface DocumentUploadProps {
  onUpload: (file: File) => void;
  isProcessing: boolean;
}
export function DocumentUpload({
  onUpload,
  isProcessing
}: DocumentUploadProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      setSelectedFile(files[0]);
    }
  }, []);
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setSelectedFile(files[0]);
    }
  };
  const handleRemoveFile = () => {
    setSelectedFile(null);
  };
  const handleExtract = () => {
    if (selectedFile) {
      onUpload(selectedFile);
    }
  };
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -20
  }} className="w-full max-w-2xl mx-auto" data-id="element-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8" data-id="element-7">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2" data-id="element-8">
          Upload Document
        </h2>
        <p className="text-gray-600 mb-6" data-id="element-9">
          Upload a document to extract and review its information
        </p>

        <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:border-gray-400'}`} data-id="element-10">
          {!selectedFile ? <>
              <UploadCloudIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" data-id="element-11" />
              <p className="text-gray-700 font-medium mb-2" data-id="element-12">
                Drag and drop your document here
              </p>
              <p className="text-gray-500 text-sm mb-4" data-id="element-13">or</p>
              <label className="inline-block" data-id="element-14">
                <input type="file" accept=".pdf,.docx,.doc,.png,.jpg,.jpeg" onChange={handleFileSelect} className="hidden" data-id="element-15" />
                <span className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium cursor-pointer hover:bg-blue-700 transition-colors inline-block" data-id="element-16">
                  Choose File
                </span>
              </label>
              <p className="text-gray-400 text-xs mt-4" data-id="element-17">
                Supported formats: PDF, DOCX, PNG, JPG (Max 10MB)
              </p>
            </> : <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} className="flex items-center justify-between bg-white rounded-lg p-4 border border-gray-200" data-id="element-18">
              <div className="flex items-center gap-3" data-id="element-19">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-20">
                  <FileIcon className="w-5 h-5 text-blue-600" data-id="element-21" />
                </div>
                <div className="text-left" data-id="element-22">
                  <p className="font-medium text-gray-900" data-id="element-23">
                    {selectedFile.name}
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-24">
                    {formatFileSize(selectedFile.size)}
                  </p>
                </div>
              </div>
              <button onClick={handleRemoveFile} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Remove file" data-id="element-25">
                <XIcon className="w-5 h-5 text-gray-500" data-id="element-26" />
              </button>
            </motion.div>}
        </div>

        {selectedFile && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mt-6" data-id="element-27">
            <button onClick={handleExtract} disabled={isProcessing} className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-28">
              {isProcessing ? <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-29" />
                  Processing...
                </> : 'Extract Fields'}
            </button>
          </motion.div>}
      </div>
    </motion.div>;
}